import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, ArrowLeft, LogOut, Building2, Users, UserPlus, 
  Mail, Trash2, Crown, BarChart3, UserRound, Copy, Plus, School, Loader2,
  CheckCircle2, Clock, MessageCircle, ExternalLink
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function PracticeAdmin({ onLogout }) {
  const navigate = useNavigate();
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [noPractice, setNoPractice] = useState(false);
  const [showCreatePractice, setShowCreatePractice] = useState(false);
  const [practiceName, setPracticeName] = useState('');
  const [creatingPractice, setCreatingPractice] = useState(false);
  // PFP seat licence (Phase 4 — school pre-purchases PFP seats for parents)
  const [pfpSeats, setPfpSeats] = useState(null);
  const [showBuySeatsDialog, setShowBuySeatsDialog] = useState(false);
  const [seatsToBuy, setSeatsToBuy] = useState(5);
  const [buyingSeats, setBuyingSeats] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchPractice();
  }, []);

  useEffect(() => {
    if (practice?.admin_id && user?.id && practice.admin_id === user.id) {
      fetchPfpSeats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practice?.id]);

  const fetchPfpSeats = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/practice/pfp-seats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPfpSeats(r.data);
    } catch (err) {
      // Non-admins get 403 — that's fine, just don't show the panel.
      if (err?.response?.status !== 403) {
        // Silent — PFP is optional
      }
    }
  };

  const startPfpCheckout = async () => {
    const n = Number(seatsToBuy);
    if (!Number.isFinite(n) || n < 1) {
      toast.error('Enter a valid number of seats');
      return;
    }
    setBuyingSeats(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/practice/pfp-seats/checkout`,
        { seats: Math.floor(n), billing_cycle: 'monthly' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = r.data?.authorization_url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Paystack returned no checkout URL');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
    } finally {
      setBuyingSeats(false);
    }
  };

  const fetchPractice = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/practices/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setPractice(response.data);
      } else {
        // No practice - check if user has clinical plan
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (userResponse.data.plan === 'clinical') {
          // Has clinical plan but no practice - show create option
          setNoPractice(true);
          setShowCreatePractice(true);
        } else {
          // Not on clinical plan
          toast.info('You need a Clinical plan to access Practice Admin');
          navigate('/settings');
        }
      }
    } catch (error) {
      toast.error('Failed to load practice data');
    } finally {
      setLoading(false);
    }
  };

  const createPractice = async () => {
    if (!practiceName.trim()) {
      toast.error('Please enter a practice name');
      return;
    }
    
    setCreatingPractice(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/practices`,
        { name: practiceName.trim(), currency: 'ZAR' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Practice created successfully!');
      setNoPractice(false);
      setShowCreatePractice(false);
      
      // Refresh user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      
      // Fetch the full practice data with therapists
      const practiceResponse = await axios.get(`${API}/practices/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (practiceResponse.data) {
        setPractice(practiceResponse.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create practice');
    } finally {
      setCreatingPractice(false);
    }
  };

  const generateInviteCode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/practices/invite`, 
        { practice_id: practice.id, email: inviteEmail || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInviteCode(response.data.code);
      toast.success('Invite code generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invite');
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode);
    toast.success('Code copied to clipboard!');
  };

  const sendInvite = async () => {
    if (!inviteEmail && !inviteCode) {
      toast.error('Please enter an email or generate a code');
      return;
    }
    
    if (inviteEmail && !inviteCode) {
      await generateInviteCode();
    }
    
    toast.success(`Invite sent to ${inviteEmail || 'generated'}!`);
    setShowInviteDialog(false);
    setInviteEmail('');
    setInviteCode('');
  };

  const removeTherapist = async (therapistId) => {
    if (!window.confirm('Remove this therapist from your practice?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/practices/members/${therapistId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Therapist removed');
      fetchPractice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove therapist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-stone-600">Loading practice data...</p>
      </div>
    );
  }

  // Show Create Practice UI for Clinical users without a practice
  if (noPractice || !practice) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                data-testid="back-btn"
                onClick={() => navigate('/')}
                className="rounded-full"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-xl font-serif text-primary-900">Practice Admin</h1>
                  <p className="text-xs text-stone-500">Set up your practice</p>
                </div>
              </div>
            </div>
            <Button variant="outline" data-testid="logout-btn" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </nav>

        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card className="p-8 rounded-3xl bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-purple-600" />
                </div>
                <h2 className="text-2xl font-serif text-purple-900 mb-2">Create Your Practice</h2>
                <p className="text-purple-700">
                  Set up your practice to start inviting team members. You'll be the practice administrator.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-purple-900">Practice Name</label>
                  <input
                    type="text"
                    placeholder="e.g., Sunshine Speech Therapy"
                    value={practiceName}
                    onChange={(e) => setPracticeName(e.target.value)}
                    className="w-full h-12 px-4 rounded-xl border border-purple-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="practice-name-input"
                  />
                </div>

                <div className="bg-white/60 rounded-xl p-4 text-sm text-purple-800">
                  <p className="font-medium mb-2">What you get with Clinical plan:</p>
                  <ul className="space-y-1 list-disc list-inside text-xs">
                    <li>5 therapist seats included (add more anytime)</li>
                    <li>Shared client data across your team</li>
                    <li>Practice-wide analytics dashboard</li>
                    <li>Generate invite codes for team members</li>
                  </ul>
                </div>

                <Button
                  className="w-full h-12 rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={createPractice}
                  disabled={creatingPractice || !practiceName.trim()}
                  data-testid="create-practice-btn"
                >
                  {creatingPractice ? 'Creating Practice...' : 'Create Practice'}
                </Button>
              </div>
            </Card>
          </motion.div>
        </main>
      </div>
    );
  }

  const seatsUsed = practice.therapists?.length || 0;
  const totalSeats = practice.seats_included + (practice.extra_seats || 0);
  const seatsAvailable = totalSeats - seatsUsed;
  const isAdmin = practice.admin_id === user?.id;
  const needsExtraSeats = (practice.extra_seats || 0) > 0;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl font-serif text-primary-900">Practice Admin</h1>
                <p className="text-xs text-stone-500">{practice.name}</p>
              </div>
            </div>
          </div>
          <Button variant="outline" data-testid="logout-btn" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
                  Clinical Plan
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
                {practice.name}
              </h2>
              <p className="text-lg text-stone-600">Manage your practice team and usage</p>
            </div>
            {isAdmin && (
              <Button 
                onClick={() => setShowInviteDialog(true)}
                className="rounded-full bg-purple-600 hover:bg-purple-700"
                data-testid="invite-therapist-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Therapist
              </Button>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-white rounded-2xl border border-stone-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-stone-500">Therapists</p>
                <Users className="w-4 h-4 text-purple-500" />
              </div>
              <p className="text-3xl font-bold text-primary-900">{seatsUsed}</p>
              <p className="text-xs text-stone-400">of {totalSeats} seats</p>
              <Progress value={(seatsUsed / totalSeats) * 100} className="h-1.5 mt-2" />
            </Card>

            <Card className="p-4 bg-white rounded-2xl border border-stone-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-stone-500">Total Children</p>
                <UserRound className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-3xl font-bold text-primary-900">{practice.total_children}</p>
              <p className="text-xs text-stone-400">across practice</p>
            </Card>

            <Card className="p-4 bg-white rounded-2xl border border-stone-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-stone-500">AI Activities</p>
                <BarChart3 className="w-4 h-4 text-green-500" />
              </div>
              <p className="text-3xl font-bold text-primary-900">{practice.ai_activities_used}</p>
              <p className="text-xs text-stone-400">of 150 this month</p>
              <Progress value={(practice.ai_activities_used / 150) * 100} className="h-1.5 mt-2" />
            </Card>

            <Card className="p-4 bg-white rounded-2xl border border-stone-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-stone-500">AI Reports</p>
                <BarChart3 className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-3xl font-bold text-primary-900">{practice.ai_reports_used}</p>
              <p className="text-xs text-stone-400">of 50 this month</p>
              <Progress value={(practice.ai_reports_used / 50) * 100} className="h-1.5 mt-2" />
            </Card>
          </div>

          {/* Seats Info */}
          {needsExtraSeats && (
            <Card className="p-4 mb-6 bg-amber-50 rounded-2xl border border-amber-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-amber-900">Extra Seats Active</p>
                  <p className="text-sm text-amber-700">
                    You have {practice.extra_seats} extra seat(s) at additional cost.
                  </p>
                </div>
                <Button variant="outline" className="rounded-full border-amber-300 text-amber-700">
                  Manage Billing
                </Button>
              </div>
            </Card>
          )}

          {/* PFP Seat Licence (Phase 4) — schools pre-pay for parent PFP access */}
          {isAdmin && pfpSeats && (
            <PfpSeatsPanel
              seats={pfpSeats}
              onBuy={() => {
                setSeatsToBuy(Math.max(1, pfpSeats.seats_purchased || 5));
                setShowBuySeatsDialog(true);
              }}
              onRefresh={fetchPfpSeats}
            />
          )}

          {/* Parents at your school — invited via /clinical/parent-links */}
          <PracticeParentsPanel />

          {/* Therapists List */}
          <Card className="rounded-3xl border border-stone-100 bg-white overflow-hidden">
            <div className="p-6 border-b border-stone-100">
              <h3 className="text-xl font-serif text-primary-900">Team Members</h3>
              <p className="text-sm text-stone-500">{seatsAvailable} seat(s) available</p>
            </div>
            
            <div className="divide-y divide-stone-100">
              {practice.therapists.map((therapist, index) => (
                <motion.div
                  key={therapist.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <UserRound className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-primary-900">{therapist.name}</p>
                        {therapist.role === 'admin' && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            Admin
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-stone-500">{therapist.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-medium text-primary-900">{therapist.children_count} children</p>
                      <p className="text-xs text-stone-400">Joined {therapist.joined ? new Date(therapist.joined).toLocaleDateString() : 'N/A'}</p>
                    </div>
                    
                    {isAdmin && therapist.role !== 'admin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTherapist(therapist.id)}
                        className="rounded-full h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`remove-therapist-${therapist.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {seatsAvailable > 0 && isAdmin && (
              <div className="p-4 bg-stone-50 border-t border-stone-100">
                <button
                  onClick={() => setShowInviteDialog(true)}
                  className="w-full p-4 border-2 border-dashed border-stone-200 rounded-xl text-stone-500 hover:border-purple-300 hover:text-purple-600 transition-colors flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-5 h-5" />
                  Add team member ({seatsAvailable} seat{seatsAvailable > 1 ? 's' : ''} available)
                </button>
              </div>
            )}
          </Card>

          {/* Practice License Notice */}
          <Card className="mt-6 p-4 bg-purple-50 rounded-2xl border border-purple-100">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-purple-700">
                <p className="font-medium mb-1">Clinical License Terms</p>
                <p>This license is valid for therapists employed within {practice.name}. All team members share access to practice data. Adding therapists from other organizations is a violation of the license agreement.</p>
              </div>
            </div>
          </Card>
        </motion.div>
      </main>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Invite Therapist</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {seatsAvailable <= 0 ? (
              <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                <p className="text-sm text-amber-800">
                  You've used all included seats. Adding another therapist will incur an additional monthly charge.
                </p>
              </div>
            ) : (
              <p className="text-sm text-stone-600">
                You have {seatsAvailable} seat{seatsAvailable > 1 ? 's' : ''} available in your plan.
              </p>
            )}

            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="colleague@practice.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="h-12 rounded-xl"
                data-testid="invite-email-input"
              />
            </div>

            <div className="text-center text-sm text-stone-400">or</div>

            <div className="space-y-2">
              <Label>Generate Invite Code</Label>
              <div className="flex gap-2">
                <Input
                  value={inviteCode}
                  readOnly
                  placeholder="Click to generate"
                  className="h-12 rounded-xl font-mono"
                />
                {inviteCode ? (
                  <Button
                    variant="outline"
                    className="h-12 rounded-xl"
                    onClick={copyInviteCode}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="h-12 rounded-xl"
                    onClick={generateInviteCode}
                  >
                    Generate
                  </Button>
                )}
              </div>
              <p className="text-xs text-stone-400">Share this code with your colleague to join</p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => setShowInviteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700"
              onClick={sendInvite}
              disabled={!inviteEmail && !inviteCode}
              data-testid="send-invite-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buy PFP seats dialog */}
      <Dialog open={showBuySeatsDialog} onOpenChange={setShowBuySeatsDialog}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="buy-pfp-seats-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Buy PFP seats for your families</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-stone-600">
              Every seat = 1 parent at your practice getting full PlayFirst Parent access
              (WhatsApp play ideas, weekly summaries, activity library). Parents you invite
              are auto-sponsored while you have seat headroom — they see no bill.
            </p>
            {pfpSeats?.price_per_seat ? (
              <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                <p className="text-sm text-emerald-800">
                  Your rate: <strong>R{(pfpSeats.price_per_seat / 100).toFixed(2)}</strong>{' '}
                  per seat per {pfpSeats.billing_cycle}
                </p>
              </div>
            ) : (
              <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                <p className="text-sm text-amber-800">
                  PFP seat pricing hasn't been configured for your practice yet.
                  Please contact PlayFirst support to enable it.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Number of seats</Label>
              <Input
                type="number"
                min="1"
                max="1000"
                value={seatsToBuy}
                onChange={(e) => setSeatsToBuy(e.target.value)}
                className="h-12 rounded-xl"
                data-testid="buy-seats-count"
              />
              {pfpSeats?.price_per_seat && (
                <p className="text-xs text-stone-500">
                  Total: <strong>
                    R{((Number(seatsToBuy || 0) * pfpSeats.price_per_seat) / 100).toFixed(2)}
                  </strong> per {pfpSeats.billing_cycle}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => setShowBuySeatsDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700"
              onClick={startPfpCheckout}
              disabled={buyingSeats || !pfpSeats?.price_per_seat}
              data-testid="pfp-checkout-btn"
            >
              {buyingSeats ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Continue to Paystack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Parents at your school panel ------------------------------------------
function PracticeParentsPanel() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchParents(); }, []);

  const fetchParents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/practice/parents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data?.parents || []);
    } catch (err) {
      // 403 is fine — non-admin non-member therapist
      if (err?.response?.status !== 403) {
        // Silent otherwise; panel just doesn't render count
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const activeCount = (rows || []).filter((r) => r.status === 'active').length;
  const pendingCount = (rows || []).filter((r) => r.status === 'pending').length;
  const parentInitiatedPending = (rows || []).filter(
    (r) => r.status === 'pending' && r.initiated_by === 'parent'
  ).length;

  return (
    <Card
      className="p-6 mb-6 rounded-3xl border border-stone-100 bg-white"
      data-testid="practice-parents-panel"
    >
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xl font-serif text-primary-900">Parents at your school</h3>
            {parentInitiatedPending > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">
                {parentInitiatedPending} awaiting approval
              </span>
            )}
          </div>
          <p className="text-sm text-stone-600 max-w-lg">
            Every parent your team invites uses 1 PFP seat. Parents receive a
            magic-link email — they just click, set a password, and they're in.
          </p>
        </div>
        <Button
          onClick={() => navigate('/clinical/parent-links')}
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="invite-parent-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Invite a parent
        </Button>
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
        </div>
      ) : (rows || []).length === 0 ? (
        <div className="p-6 border-2 border-dashed border-stone-200 rounded-2xl text-center">
          <Users className="w-8 h-8 mx-auto mb-2 text-stone-300" />
          <p className="text-sm text-stone-500">
            No parents invited yet. Click "Invite a parent" above to get started.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4 text-center">
            <div className="p-3 bg-emerald-50 rounded-xl">
              <p className="text-2xl font-bold text-emerald-700" data-testid="parents-active-count">{activeCount}</p>
              <p className="text-xs text-emerald-800">Active</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl">
              <p className="text-2xl font-bold text-amber-700" data-testid="parents-pending-count">{pendingCount}</p>
              <p className="text-xs text-amber-800">Pending</p>
            </div>
            <div className="p-3 bg-stone-50 rounded-xl">
              <p className="text-2xl font-bold text-stone-700">{(rows || []).length}</p>
              <p className="text-xs text-stone-600">Total</p>
            </div>
          </div>
          <div className="divide-y divide-stone-100 border rounded-2xl border-stone-100 overflow-hidden">
            {(rows || []).slice(0, 8).map((p) => (
              <div key={p.id} className="p-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm text-primary-900 truncate">
                      {p.parent_email || p.parent_name || 'Unknown'}
                    </p>
                    {p.status === 'active' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </span>
                    )}
                    {p.status === 'pending' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full">
                        <Clock className="w-3 h-3" /> Pending
                      </span>
                    )}
                    {p.initiated_by === 'parent' && p.status === 'pending' && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                        Parent-initiated
                      </span>
                    )}
                    {p.parent_whatsapp_verified && (
                      <MessageCircle className="w-3 h-3 text-emerald-600" />
                    )}
                  </div>
                  <p className="text-xs text-stone-500 truncate">
                    Therapist: {p.therapist_name}
                    {p.child_name ? ` · ${p.child_name}` : ''}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className="rounded-full"
                >
                  <a href={`mailto:${p.parent_email || ''}`}>
                    <Mail className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            ))}
          </div>
          {(rows || []).length > 8 && (
            <p className="text-xs text-stone-400 text-center mt-2">
              Showing 8 of {rows.length}. Go to Client Portal → Parent Links for the full list.
            </p>
          )}
          <Button
            variant="link"
            onClick={() => navigate('/clinical/parent-links')}
            className="text-sm p-0 h-auto mt-3"
            data-testid="open-parent-links"
          >
            Manage parent links <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </>
      )}
    </Card>
  );
}

function PfpSeatsPanel({ seats, onBuy, onRefresh }) {
  const priceR = seats.price_per_seat ? (seats.price_per_seat / 100).toFixed(2) : null;
  const purchased = seats.seats_purchased || 0;
  const used = seats.seats_used || 0;
  const available = Math.max(0, purchased - used);
  const active = !!seats.active;
  const configured = !!seats.configured;

  return (
    <Card
      className="p-6 mb-6 rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50"
      data-testid="pfp-seats-panel"
    >
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <School className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-serif text-emerald-900">PlayFirst Parent — school seats</h3>
              {active ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded-full">
                  Active
                </span>
              ) : configured ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">
                  Not subscribed yet
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded-full">
                  Pricing not configured
                </span>
              )}
            </div>
            <p className="text-sm text-emerald-800 max-w-md">
              Pre-pay PFP access for families at your practice. Parents you invite
              (with a linked clinical child) will be auto-sponsored — no bill for them.
            </p>
            {active && (
              <div className="flex flex-wrap gap-4 mt-3 text-sm">
                <span className="text-emerald-900" data-testid="pfp-seats-count">
                  <strong>{used}</strong> / {purchased} seats used
                  {available > 0 ? ` (${available} available)` : ''}
                </span>
                {priceR && (
                  <span className="text-emerald-900">
                    R{priceR} / seat / {seats.billing_cycle}
                  </span>
                )}
                {seats.next_billing_at && (
                  <span className="text-emerald-700 text-xs">
                    Next renewal: {new Date(seats.next_billing_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
            {!active && configured && (
              <p className="text-sm text-emerald-900 mt-2">
                <strong>R{priceR}</strong> per seat per {seats.billing_cycle} — subscribe below to activate.
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            onClick={onBuy}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="buy-pfp-seats-btn"
            disabled={!configured}
          >
            {active ? 'Add / adjust seats' : 'Subscribe'}
          </Button>
          {active && (
            <Button
              variant="ghost"
              onClick={onRefresh}
              size="sm"
              className="rounded-full text-xs"
              data-testid="refresh-pfp-seats"
            >
              Refresh
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

